import React, { useState, useEffect, useCallback } from 'react';
import { 
  Typography, 
  Box, 
  Tabs,
  Tab,
  CircularProgress,
  Alert
} from '@mui/material';
import { 
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import BugTrendsView from '../workitems/BugTrendsView';
import SprintReportDashboard from './SprintReportDashboard';
import TsiDashboard from './TsiDashboard';
import EpicGanttChart from './EpicGanttChart';
import WorkItemsView from '../workitems/WorkItemsView';
import { getEpics, getTimelineConfig } from '../../services/apiService';

/**
 * Dashboard component for displaying metrics and charts
 * @returns {JSX.Element} Dashboard component
 */
const Dashboard = () => {
  const [tabValue, setTabValue] = useState(0); // Default to Delivery tab (index 0)
  const [deliverySubTab, setDeliverySubTab] = useState(1); // Sub-tab within Delivery: 0 = Gantt Chart, 1 = Work Items (default to Work Items)
  const [epics, setEpics] = useState([]);
  const [timelineConfig, setTimelineConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Handle main tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle delivery sub-tab change
  const handleDeliverySubTabChange = (event, newValue) => {
    setDeliverySubTab(newValue);
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load timeline config and epics in parallel
      // Use authenticated API calls for Dashboard
      const [configData, epicsData] = await Promise.all([
        getTimelineConfig(),
        getEpics({ hasTargetDate: 'true' }, true) // true = use auth
      ]);
      
      setTimelineConfig(configData);
      setEpics(epicsData || []);
    } catch (err) {
      console.error('Error loading delivery data:', err);
      setError('Failed to load epics and timeline configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load epics and timeline config when Delivery tab is active and Gantt Chart sub-tab is selected
  useEffect(() => {
    if (tabValue === 0 && deliverySubTab === 0) {
      loadData();
    }
  }, [tabValue, deliverySubTab, loadData]);

  // Listen for timeline config updates and reload data
  useEffect(() => {
    const handleTimelineConfigUpdate = () => {
      // Only reload if Delivery tab is currently active and Gantt Chart sub-tab is selected
      if (tabValue === 0 && deliverySubTab === 0) {
        console.log('Timeline config updated, reloading delivery data...');
        loadData();
      }
    };

    window.addEventListener('timelineConfigUpdated', handleTimelineConfigUpdate);

    return () => {
      window.removeEventListener('timelineConfigUpdated', handleTimelineConfigUpdate);
    };
  }, [tabValue, deliverySubTab, loadData]);
  
  // Render the Delivery tab content (Gantt chart or Work Items)
  const renderDeliveryTab = () => {
    return (
      <Box>
        <Tabs value={deliverySubTab} onChange={handleDeliverySubTabChange} sx={{ mb: 3 }}>
          <Tab label="Gantt Chart" />
          <Tab label="Work Items" />
        </Tabs>
        
        {deliverySubTab === 0 && (
          <>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : (
              <EpicGanttChart epics={epics} timelineConfig={timelineConfig} />
            )}
          </>
        )}
        
        {deliverySubTab === 1 && (
          <WorkItemsView />
        )}
      </Box>
    );
  };
  
  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <DashboardIcon sx={{ mr: 1, color: "#1D4434" }} />
        <Typography variant="h4" gutterBottom>
          Home
        </Typography>
      </Box>
      
      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Delivery" />
        <Tab label="Production Defects" />
        <Tab label="Sprint Reports" />
        <Tab label="TSI Dashboard" />
      </Tabs>
      
      {tabValue === 0 && renderDeliveryTab()}
      {tabValue === 1 && <BugTrendsView />}
      {tabValue === 2 && <SprintReportDashboard />}
      {tabValue === 3 && <TsiDashboard />}
    </Box>
  );
};

export default Dashboard;
